/**
 * Inline NodeLayout component for singular tool call operations.
 * Renders the full NodeLayout directly in the accordion content.
 * Supports both pending and confirmed/rejected changes (historical diffs).
 */

import { useLazyQuery } from "@apollo/client/react";
import { AnimatePresence, motion } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  FileDiffIcon,
  FileTextIcon,
  FoldVerticalIcon,
  UnfoldVerticalIcon,
} from "lucide-react";

import { FieldDelta, IObjectWithTypename } from "@/components/@types";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import type { NodeTypeIconChange } from "@/components/organisms/Node/NodeTitleLayoutEntityBox";
import { usePendingSession } from "@/components/organisms/PendingSession/PendingSession.store";
import { Queries } from "@/constants";
import { idToTypename, identifierAndTypenameToId } from "@/objects/@utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import {
  usePendingChangeStatus,
  useReportStuck,
  useStickyBarBottom,
} from "./ToolCallCard.hooks";
import type { NodeItem } from "./ToolCallCard.types";
import { inferTypename } from "./ToolCallCard.tools";

/**
 * Renders a NodeLayout inline for a single item from a tool call output.
 *
 * When `pendingChangeIdentifier` is provided, the component queries that
 * specific PendingChange for its deltas instead of using the node's
 * `pendingChange` FK (which always points to the *latest* change and
 * would show incorrect deltas for earlier sequential operations).
 *
 * `pendingChangeType` is used as authoritative changeType when the
 * specific PendingChange query hasn't returned yet (or returns null),
 * preventing a "create" card from showing "update" deltas.
 */
export function ToolCallCardNodeLayout({
  item,
  toolName,
  deltas,
  deltaMode: deltaModeOverride,
  size = "sm",
  pendingChangeIdentifier,
  pendingChangeType,
  titleStartDecorator,
  titleEndDecorator,
  onTitleClick,
  hideBody,
  hideEmptyFields,
  loadOnOpen = false,
  defaultShowsRecord = false,
  defaultFieldsExpanded = false,
  isTypeIconVisible = false,
  typeIconChange = null,
  hideRecordTools = false,
  contextEntityIdentifier = null,
}: {
  item: NodeItem;
  toolName?: string | null;
  /** Field deltas to show before/after values */
  deltas?: FieldDelta[];
  /** Force delta mode (3-column layout) on/off. Default: auto-detect from deltas */
  deltaMode?: boolean;
  /** Size variant for spacing and typography. Default: "sm" */
  size?: "sm" | "md";
  /** Identifier of the specific PendingChange to render deltas for */
  pendingChangeIdentifier?: string | null;
  /** Authoritative change type from the content's pendingChange (avoids misattributed deltas) */
  pendingChangeType?: string | null;
  /** Content rendered before the title (e.g. expand chevron) */
  titleStartDecorator?: React.ReactNode;
  /**
   * Status at the end of the title bar (e.g. a change badge): after the
   * identifier and actions menu, just before the expand toggle.
   */
  titleEndDecorator?: React.ReactNode;
  /** Click handler for the title bar */
  onTitleClick?: () => void;
  /** When true, only render the title bar — hide the NodeLayout body */
  hideBody?: boolean;
  /** When true, filter out fields whose value is empty/null */
  hideEmptyFields?: boolean;
  /** Show the record's type icon beside its entity box. */
  isTypeIconVisible?: boolean;
  /** A change to the record, said on its type icon. */
  typeIconChange?: NodeTypeIconChange | null;
  /** The record page's own tools - its search index status, its ID to copy - left off its title. */
  hideRecordTools?: boolean;
  /** The business the record is shown in: one of its own needs no badge. */
  contextEntityIdentifier?: string | null;
  /**
   * The item already names the record (its title fields and entity): draw the
   * title bar from it, and read the record only once the body is opened.
   */
  loadOnOpen?: boolean;
  /** A change drawn first as the whole record - every tab - rather than as what it changed. */
  defaultShowsRecord?: boolean;
  /** Every field shown from the first, not folded behind its section. */
  defaultFieldsExpanded?: boolean;
}) {
  const typename = inferTypename(item, toolName);
  const base64Id = identifierAndTypenameToId(item.identifier, typename);
  const pendingSessionId = usePendingSession()?.pendingSessionId ?? null;
  // Its tabs stick under the title, which sticks under the card's header.
  const measureTitle = useStickyBarBottom(
    "--node-title-bottom",
    "--tool-call-header-bottom",
  );
  // Stuck, the box's rounded top has scrolled away under the card's header,
  // which draws it again (``STUCK_RECORD_TOP_CLASS``). Only then: at rest that
  // border is still there, and a second would double it. Only while the body
  // is open: a closed record has nothing to scroll under its title.
  const [titleBar, setTitleBar] = useState<HTMLDivElement | null>(null);
  const titleRef = useCallback(
    (bar: HTMLDivElement | null) => {
      measureTitle(bar);
      setTitleBar(bar);
    },
    [measureTitle],
  );
  const reportStuck = useReportStuck();
  const watchesStuck = !hideBody && reportStuck !== null;
  useEffect(() => {
    const record = titleBar?.parentElement;
    if (!watchesStuck || !titleBar || !record) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const isStuck =
        record.getBoundingClientRect().top <
        titleBar.getBoundingClientRect().top - 0.5;
      titleBar.dataset.stuck = String(isStuck);
      // The card's header draws the record's rounded top while it is stuck.
      reportStuck?.(item.identifier ?? "", isStuck);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    // Scrolls do not bubble, but they are caught on the way down.
    document.addEventListener("scroll", schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      delete titleBar.dataset.stuck;
      reportStuck?.(item.identifier ?? "", false);
    };
  }, [titleBar, watchesStuck, reportStuck, item.identifier]);

  const [load, { data, loading, called }] = useLazyQuery(
    Queries.NODE_QUERY_WITH_PENDING_CHANGE,
    {
      fetchPolicy: "cache-first",
      // ``relay`` ``node(id: ...)`` returns ``null`` (rather than erroring)
      // when the underlying record is missing or filtered out by entity
      // scope. Treating that as a ``notFound`` instead of perpetual loading
      // is what powers the "Record not found" state below.
      errorPolicy: "all",
    },
  );

  const waitsToOpen = loadOnOpen && hideBody !== false;
  useEffect(() => {
    if (item.identifier && !waitsToOpen) {
      load({
        variables: {
          id: base64Id,
          // What the agent drafted under the record in this turn shows with it.
          pendingSession: pendingSessionId,
        },
      });
    }
  }, [item.identifier, base64Id, load, waitsToOpen, pendingSessionId]);

  const itemAsNode = useMemo(
    () =>
      loadOnOpen
        ? ({
            ...item,
            __typename: typename,
            id: base64Id,
          } as IObjectWithTypename)
        : undefined,
    [loadOnOpen, item, typename, base64Id],
  );
  const { store } = useNodeSetup({
    mode: "update",
    node: (data?.node ?? itemAsNode) as IObjectWithTypename,
    typename: idToTypename(base64Id),
  });
  const { actions: defaultActions, fieldDefinitions: rawFieldDefinitions } =
    useNode(store);
  // The record as it now is, every tab of it - a statement's lines are in a
  // tab its change's fields do not show - in a store of its own, which the
  // change's historical values are never written over.
  const { store: recordStore } = useNodeSetup({
    mode: "update",
    node: (data?.node ?? itemAsNode) as IObjectWithTypename,
    typename: idToTypename(base64Id),
  });
  const { fieldDefinitions: recordFieldDefinitions } = useNode(recordStore);

  const fieldDefinitions = useMemo(() => {
    if (!hideEmptyFields) return rawFieldDefinitions;
    const fv = store.getState().fieldValues;
    return rawFieldDefinitions.filter((fd) => {
      const val = fv[(fd as { objectKey?: string }).objectKey ?? ""];
      if (val == null || val === "") return false;
      if (Array.isArray(val) && val.length === 0) return false;
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        Object.keys(val).length === 0
      )
        return false;
      return true;
    });
  }, [hideEmptyFields, rawFieldDefinitions, store]);

  const {
    activePendingChange,
    changeType,
    changeStatus,
    changeIdentifier,
    isCreate,
    isHistorical,
    nodeDeltas,
    showsStatus,
    hasChanges,
    isLoadingChange,
  } = usePendingChangeStatus({
    node: data?.node,
    toolName,
    pendingChangeIdentifier,
    pendingChangeType,
  });

  const effectiveDeltas = deltas ?? nodeDeltas;
  const hasDeltas = effectiveDeltas && effectiveDeltas.length > 0;
  const isChange =
    deltaModeOverride ?? (isCreate ? true : (hasDeltas ?? false));
  // A change is drawn as what it changed, or as the whole record where the
  // caller starts it there (`defaultShowsRecord`); toggled, the other.
  const [showsRecord, setShowsRecord] = useState(defaultShowsRecord);
  const deltaMode = isChange && !showsRecord;

  // Before paint: written after it, the first frame drew the diff with no
  // deltas to fold around, so every field showed and then collapsed.
  useLayoutEffect(() => {
    if (!data?.node) return;
    if (!deltaMode) return;

    const currentFieldValues = store.getState().fieldValues;

    // When resultingData is available (the PendingChange was superseded by a
    // later change, or it's a create whose object was later modified), override
    // field values with the historical snapshot so the card shows the state at
    // that point in time rather than the live object state.
    const resultingData = activePendingChange?.resultingData as
      | Record<string, unknown>
      | null
      | undefined;
    let overriddenFieldValues = currentFieldValues;
    if (resultingData) {
      const snakeToCamel = (s: string) =>
        s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
      const overrides: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(resultingData)) {
        const camelKey = snakeToCamel(key);
        if (camelKey in currentFieldValues && camelKey !== "pendingChange") {
          overrides[camelKey] = value;
        }
      }
      overriddenFieldValues = { ...currentFieldValues, ...overrides };
    }

    store.setState({
      fieldValues: {
        ...overriddenFieldValues,
        pendingChange: {
          identifier: changeIdentifier ?? item.identifier,
          changeType: changeType ?? "update",
          status:
            (changeStatus as "pending" | "confirmed" | "rejected") ?? "pending",
          objectIdentifier: item.identifier,
          modelName: typename.replace("Object", "").toLowerCase(),
          resultingData: resultingData ?? null,
          deltas: effectiveDeltas ?? [],
        },
      },
    });
  }, [
    deltaMode,
    changeType,
    changeStatus,
    changeIdentifier,
    effectiveDeltas,
    activePendingChange?.resultingData,
    data?.node,
    store,
    item.identifier,
    typename,
  ]);

  const [fieldsExpanded, setFieldsExpanded] = useState(defaultFieldsExpanded);

  const recordToggle = isChange ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="icon-xs"
          variant={showsRecord ? "secondary" : "ghost"}
          aria-pressed={showsRecord}
          aria-label={
            showsRecord ? "Show what changed" : "Show the whole record"
          }
          data-testid="record-or-change"
          onClick={(e) => {
            e.stopPropagation();
            setShowsRecord((value) => !value);
          }}
        >
          {showsRecord ? (
            <FileDiffIcon size={12} />
          ) : (
            <FileTextIcon size={12} />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {showsRecord ? "Show what changed" : "Show the whole record"}
      </TooltipContent>
    </Tooltip>
  ) : null;

  const toggleButton = (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={
        hideBody !== undefined
          ? hideBody
            ? "Open record"
            : "Close record"
          : fieldsExpanded
            ? "Collapse fields"
            : "Expand fields"
      }
      onClick={(e) => {
        e.stopPropagation();
        if (hideBody !== undefined) {
          onTitleClick?.();
        } else {
          setFieldsExpanded((v) => !v);
        }
      }}
    >
      {fieldsExpanded ? (
        <FoldVerticalIcon size={12} />
      ) : (
        <UnfoldVerticalIcon size={12} />
      )}
    </Button>
  );

  // Three states from the node query:
  // - not yet resolved (called && loading, or never called) → Skeleton
  // - resolved to null (deleted, scoped out, or never existed) → not-found
  // - resolved to a node → render
  // The change this card names decides whether it is drawn as a diff, so it
  // waits for that as well: drawn before it, the card showed every field and
  // then folded them away. One that names its record draws its title from the
  // first, and its opened fields as skeletons until both are back.
  const isInitialLoad = !loadOnOpen && (!called || isLoadingChange);
  const isMissing = called && !loading && !data?.node;

  if (isInitialLoad) {
    return (
      <div className={cn(size === "sm" ? "p-3" : "p-4")}>
        <Skeleton
          variant="rectangular"
          height={size === "sm" ? 100 : 140}
          className="rounded-md"
        />
      </div>
    );
  }

  if (isMissing) {
    const rawLabel = (toolName ?? "")
      .replace(/^(create|update|delete|query)_/, "")
      .replace(/_/g, " ")
      .trim();
    // Crude singularization that lines up with ``formatToolName`` so the
    // empty-state copy reads "this contact" instead of "this contacts".
    const singularize = (s: string) => {
      if (s.endsWith("ies")) return s.slice(0, -3) + "y";
      if (
        s.endsWith("ses") ||
        s.endsWith("xes") ||
        s.endsWith("ches") ||
        s.endsWith("shes")
      )
        return s.slice(0, -2);
      if (s.endsWith("s") && !s.endsWith("ss")) return s.slice(0, -1);
      return s;
    };
    const noun = rawLabel ? singularize(rawLabel) : "record";
    return (
      <div
        className={cn(
          size === "sm" ? "p-3" : "p-4",
          "flex items-center justify-center gap-1.5 bg-muted",
        )}
      >
        <Typography level="body-xs" className="text-muted-foreground/80 italic">
          This {noun} is no longer available — it may have been deleted, been
          rejected as a proposed change, or is outside the current entity scope.
        </Typography>
      </div>
    );
  }

  return (
    <div
      // ``clip`` rather than ``hidden``: it trims the card the same way, but
      // does not become a scroll container, which would stop the title row
      // below from sticking to whatever actually scrolls.
      className={cn(
        "isolate relative z-0 w-full max-w-full min-w-0 overflow-clip bg-card",
        isHistorical && "opacity-[0.85]",
      )}
    >
      <div
        ref={titleRef}
        // Held under the card's header while the record's fields scroll under
        // it, so you can see which record you are reading.
        className="sticky top-(--tool-call-header-bottom,0px) z-3"
      >
        <NodeTitleLayout
          title={item.identifier}
          store={store}
          // Until the record is back the title bar is skeletons, not the
          // identifier the row falls back on and an entity box popping in.
          isLoading={loading && !data?.node && !loadOnOpen}
          size={"sm"}
          titleLevel="body-md"
          className="p-1 pl-2 pr-2 bg-card [border-bottom-width:1px] [border-bottom-style:solid] border-border"
          startDecorator={titleStartDecorator}
          isTypeIconVisible={isTypeIconVisible}
          typeIconChange={typeIconChange}
          hideRecordTools={hideRecordTools}
          contextEntityIdentifier={contextEntityIdentifier}
          onClick={onTitleClick}
          // No actions menu while the change has a status of its own — shown in
          // the tool call card's header by ``ToolCallCardPendingStatus``.
          actions={
            deltaMode && hasChanges && showsStatus ? undefined : defaultActions
          }
          // Status and the expand toggle close the row, after the identifier
          // and the actions menu, so they line up down a list whether or not
          // a row has actions.
          trailingDecorator={
            <>
              {titleEndDecorator}
              {recordToggle}
              {toggleButton}
            </>
          }
        />
      </div>
      <AnimatePresence initial={false}>
        {!hideBody && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "clip" }}
          >
            {/* Until the record is back its fields have no values: drawn
                empty, every label showed, then folded once it arrived. */}
            {data?.node || loadOnOpen ? (
              <NodeLayout
                key={showsRecord ? "record" : "change"}
                store={showsRecord ? recordStore : store}
                fieldDefinitions={
                  showsRecord ? recordFieldDefinitions : fieldDefinitions
                }
                deltaMode={deltaMode}
                changeVariant={isCreate ? "create" : undefined}
                size={"sm"}
                forceLayoutMode="tabs"
                isTabBarSticky
                viewMode="grid"
                isLoading={loadOnOpen && (!data?.node || isLoadingChange)}
                isMultiline={false}
                isVisiblyFlush={true}
                collapseThreshold={3}
                allSectionsExpanded={fieldsExpanded}
                onAllSectionsExpandedChange={setFieldsExpanded}
              />
            ) : (
              <div className={cn(size === "sm" ? "p-3" : "p-4")}>
                <Skeleton
                  variant="rectangular"
                  height={size === "sm" ? 100 : 140}
                  className="rounded-md"
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
